import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAcceptInvitation } from '../helpers/useInvitationsApi';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { Check, AlertCircle, Home, ArrowRight } from 'lucide-react';
import styles from './join.$inviteCode.module.css';

const JoinSessionPage = () => {
  const navigate = useNavigate();
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const [error, setError] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);
  const { mutate: acceptInvitation, isPending, data } = useAcceptInvitation();

  useEffect(() => {
    if (inviteCode && !hasAttempted) {
      setHasAttempted(true);
      acceptInvitation(
        { inviteCode },
        {
          onError: (err: any) => {
            setError(
              err?.message || 'Failed to accept invitation. The link may be expired or invalid.',
            );
          },
        },
      );
    }
  }, [inviteCode, hasAttempted, acceptInvitation]);

  if (!inviteCode) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconError}>
            <AlertCircle size={48} />
          </div>
          <h2 className={styles.title}>Invalid Link</h2>
          <p className={styles.message}>
            This invitation link appears to be invalid or incomplete.
          </p>
          <Button onClick={() => navigate('/')} className={styles.button}>
            <Home size={16} />
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <Spinner size="lg" />
          <h2 className={styles.title}>Joining Session...</h2>
          <p className={styles.message}>Please wait while we process your invitation.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconError}>
            <AlertCircle size={48} />
          </div>
          <h2 className={styles.title}>Invitation Error</h2>
          <p className={styles.message}>{error}</p>
          <div className={styles.actions}>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home size={16} />
              Go Home
            </Button>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </div>
        </div>
      </div>
    );
  }

  if (data && data.session) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconSuccess}>
            <Check size={48} />
          </div>
          <h2 className={styles.title}>
            {data.alreadyJoined ? 'Already a Member!' : 'Successfully Joined!'}
          </h2>
          <p className={styles.message}>
            {data.alreadyJoined
              ? `You are already a participant in "${data.session.name}".`
              : `You have joined the session "${data.session.name}".`}
          </p>
          <p className={styles.subMessage}>
            You can now collaborate, chat with team members, and work with the AI assistant.
          </p>
          <Button onClick={() => navigate(`/session/${data.session.id}`)} className={styles.button}>
            Go to Session
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default JoinSessionPage;
